import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";
import * as SecureStore from "expo-secure-store";


const isWeb = Platform.OS === "web";
const MAX_CHUNK_SIZE = 1800;
const CHUNK_HEADER = " __chunked__:";
const chunkKey = (key: string, index: number) => `${key}.__c${index}`;

async function clearChunks(key: string) {
  const header = await SecureStore.getItemAsync(key);
  if (header?.startsWith(CHUNK_HEADER)) {
    const count = parseInt(header.slice(CHUNK_HEADER.length), 10);
    await Promise.all(
      Array.from({ length: count }, (_, i) =>
        SecureStore.deleteItemAsync(chunkKey(key, i)),
      ),
    );
  }
}

export const secureStorage = {
  async getItem(key: string): Promise<string | null> {
    if (isWeb) return AsyncStorage.getItem(key);
    const value = await SecureStore.getItemAsync(key);
    if (value === null) return null;
    if (!value.startsWith(CHUNK_HEADER)) return value;
    const count = parseInt(value.slice(CHUNK_HEADER.length), 10);
    const parts = await Promise.all(
      Array.from({ length: count }, (_, i) =>
        SecureStore.getItemAsync(chunkKey(key, i)),
      ),
    );
    return parts.join("");
  },

  async setItem(key: string, value: string): Promise<void> {
    if (isWeb) return AsyncStorage.setItem(key, value);
    // Drop any chunks left over from a previous larger value.
    await clearChunks(key);
    if (value.length <= MAX_CHUNK_SIZE) {
      await SecureStore.setItemAsync(key, value);
      return;
    }
    const count = Math.ceil(value.length / MAX_CHUNK_SIZE);
    await Promise.all(
      Array.from({ length: count }, (_, i) =>
        SecureStore.setItemAsync(
          chunkKey(key, i),
          value.slice(i * MAX_CHUNK_SIZE, (i + 1) * MAX_CHUNK_SIZE),
        ),
      ),
    );
    await SecureStore.setItemAsync(key, CHUNK_HEADER + count);
  },

  async removeItem(key: string): Promise<void> {
    if (isWeb) return AsyncStorage.removeItem(key);
    await clearChunks(key);
    await SecureStore.deleteItemAsync(key);
  },

  async multiRemove(keys: string[]): Promise<void> {
    if (isWeb) return AsyncStorage.multiRemove(keys);
    await Promise.all(keys.map((key) => this.removeItem(key)));
  },
};
